import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Bell, Euro, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog to configure CPA target + email alerts for a client.
 * Sends alerts when current_cpa > target_cpa × 1.2 (daily check at 09:00 UTC).
 */
export default function CPATargetDialog({ client, open, onOpenChange, onSaved }) {
  const [targetCpa, setTargetCpa] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    if (client) {
      setTargetCpa(client.target_cpa || 0);
      setEnabled(client.cpa_alert_enabled === true);
    }
  }, [client]);

  if (!client) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/clients/${client.id}/stats-settings`,
        { target_cpa: parseFloat(targetCpa) || 0, cpa_alert_enabled: enabled },
        { withCredentials: true }
      );
      toast.success(`Cible CPA mise à jour pour ${client.name}`);
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const triggerCheck = async () => {
    setTriggering(true);
    try {
      const { data } = await axios.post(`${API}/google-ads/cpa-alerts/check`, {}, { withCredentials: true });
      const sent = data.alerts_sent || 0;
      const checked = data.checked || 0;
      if (sent > 0) {
        toast.success(`${sent} alerte(s) envoyée(s) sur ${checked} client(s) vérifié(s)`);
      } else if (data.skipped) {
        toast.info(`Aucune vérification possible : ${data.skipped}`);
      } else {
        toast.info(`${checked} client(s) vérifié(s) — aucune alerte à envoyer`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du déclenchement");
    } finally {
      setTriggering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cpa-target-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-orange-600" />
            Cible CPA — {client.name}
          </DialogTitle>
          <DialogDescription>
            Recevez un email automatique si le coût par acquisition dépasse cette cible de plus de 20% (vérification quotidienne).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">CPA cible (€)</Label>
            <div className="relative">
              <Euro className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                min={0}
                step={5}
                value={targetCpa}
                onChange={(e) => setTargetCpa(e.target.value)}
                className="pl-8"
                placeholder="50"
                data-testid="cpa-target-input"
              />
            </div>
            <p className="text-[11px] text-neutral-500">
              Alerte déclenchée si CPA actuel &gt; <strong>{((targetCpa || 0) * 1.2).toFixed(2)} €</strong> (cible × 1.2)
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-neutral-900">Alertes email</p>
              <p className="text-xs text-neutral-500">Vérification automatique chaque jour à 09:00</p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={!targetCpa || targetCpa <= 0}
              data-testid="cpa-alert-toggle"
            />
          </div>

          {client.last_cpa_alert_sent_at && (
            <p className="text-xs text-neutral-500">
              Dernière alerte envoyée : {new Date(client.last_cpa_alert_sent_at).toLocaleString('fr-FR')}
            </p>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={triggerCheck}
              disabled={triggering}
              className="text-xs"
              data-testid="cpa-trigger-check-btn"
            >
              {triggering ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Tester maintenant
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="cpa-save-btn">
                {saving ? '...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
